import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { Button, Icon, Input, InputNumber, Popconfirm, Select } from 'antd'
import { CollapseProps } from 'antd/lib/collapse'
import { SingleCollapsibleContainer } from '../../../../../components/ResponsiveContainer/SingleCollapsibleContainer'
import ButtonGroup from 'antd/lib/button/button-group'
import { ResponsiveRow } from '../../../../../components/ResponsiveContainer/ResponsiveRow'
import { isNil } from 'lodash'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { DiscountModels } from '@getgreenline/products'
import { Checkbox, Col, Row } from 'antd-v4'
import { ExternalLoyaltyModels } from '@getgreenline/external-loyalty'

interface Props {
  discount: DiscountModels.CreateOrEditDiscount
  isDiscountUndefined: boolean
  isDiscountCodeValid: boolean | null
  collapseProps: CollapseProps
  currentTabType: DiscountModels.ApplyType
  currentCompanyStore?: CurrentCompanyStore
  setCurrentTabType: (discountType: DiscountModels.ApplyType) => void
  setEnableLoyaltyTargeting: (value: boolean) => void
  onDiscountCodeChange: (value: string) => void
}

interface State {
  isTabSwitchBlocked: boolean
  toTab: DiscountModels.ApplyType
}

@inject('currentCompanyStore')
@observer
export class AddDiscountType extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isTabSwitchBlocked: false,
      toTab: 'products',
    }
  }
  formatter = (value?: number | string) => {
    if (value === '' || isNil(value)) {
      return ''
    }

    return `${isNaN(Number(value)) ? '' : value}`
  }

  validateTabSwitch(toTab: DiscountModels.ApplyType) {
    const { discount } = this.props
    const hasProductOrCategoryItems = discount.products.length + discount.categories.length > 0

    !hasProductOrCategoryItems
      ? this.switchToTab(toTab)
      : this.setState({ isTabSwitchBlocked: true, toTab })
  }

  switchToTab(toTab: DiscountModels.ApplyType) {
    this.setState({ isTabSwitchBlocked: false })
    this.props.setCurrentTabType(toTab)
    if (toTab !== 'products') this.props.discount.setAutomaticDiscount(false)
  }

  render() {
    const {
      collapseProps,
      discount,
      currentTabType,
      currentCompanyStore,
      isDiscountCodeValid,
      onDiscountCodeChange,
    } = this.props

    const hasAlpineIQLoyaltyOn =
      currentCompanyStore?.externalLoyaltyProvider ===
      ExternalLoyaltyModels.ExternalLoyaltyProviders.ALPINE_IQ

    const hasSpringbigLoyaltyOn =
      currentCompanyStore?.externalLoyaltyProvider ===
      ExternalLoyaltyModels.ExternalLoyaltyProviders.SPRINGBIG

    const hasExternalLoyaltyEnabled = hasAlpineIQLoyaltyOn || hasSpringbigLoyaltyOn

    return (
      <SingleCollapsibleContainer
        collapseProps={collapseProps}
        panelProps={{ header: 'Type', key: 'type' }}
        defaultActive
      >
        <ResponsiveRow
          labelText={<strong>Applied to</strong>}
          rowProps={{ style: { marginBottom: 16 } }}
        >
          <Popconfirm
            visible={this.state.isTabSwitchBlocked}
            title='This will remove all of the specified products and categories'
            onCancel={() => this.setState({ isTabSwitchBlocked: false })}
            onConfirm={() => {
              discount.removeAllProducts()
              discount.removeAllCategories()
              this.switchToTab(this.state.toTab)
            }}
          >
            <ButtonGroup>
              <Button
                type={currentTabType === 'products' ? 'primary' : 'default'}
                disabled={this.props.isDiscountUndefined && currentTabType !== 'products'}
                style={{ minWidth: 100 }}
                onClick={() => {
                  this.validateTabSwitch('products')
                }}
              >
                <Icon type='tags' /> Product
              </Button>

              <Button
                type={currentTabType === 'cart' ? 'primary' : 'default'}
                disabled={this.props.isDiscountUndefined && currentTabType !== 'cart'}
                style={{ minWidth: 100 }}
                onClick={() => {
                  this.validateTabSwitch('cart')
                }}
              >
                <Icon type='shopping-cart' /> Cart
              </Button>
              <Button
                type={currentTabType === 'bundle' ? 'primary' : 'default'}
                disabled={this.props.isDiscountUndefined && currentTabType !== 'bundle'}
                style={{ minWidth: 100 }}
                onClick={() => {
                  this.validateTabSwitch('bundle')
                }}
              >
                <Icon type='shopping-cart' /> Bundle
              </Button>
            </ButtonGroup>
          </Popconfirm>

          <div className='caption'>
            {currentTabType === 'products'
              ? 'This discount will be applied by selecting the product first and attaching the discount'
              : ''}
            {currentTabType === 'cart' ? (
              <>
                <p className='mb-1'>
                  This discount will be applied to all of the items in the cart (except those
                  excluded).
                </p>
                <p style={{ marginBottom: 0 }}>
                  For fixed discounts, the value will be spread across all products.
                </p>
              </>
            ) : (
              ''
            )}
            {currentTabType === 'bundle' ? (
              <>
                <p className='mb-1'>
                  This discount will be applied to the specific products selected in the products
                  section.
                </p>
                <p style={{ marginBottom: 0 }}>
                  All products should be added to the cart in order for the discounts to show up
                </p>
              </>
            ) : (
              ''
            )}
          </div>
        </ResponsiveRow>

        <ResponsiveRow
          labelText={<strong>Amount</strong>}
          rowProps={{ style: { marginBottom: 16 } }}
        >
          <Input.Group compact>
            {discount.type === DiscountModels.DiscountTypes.PERCENT ? (
              <InputNumber
                min={0}
                max={100}
                value={discount.amount}
                onChange={(value) => {
                  discount.amount = value || 0
                }}
                formatter={this.formatter}
              />
            ) : (
              <InputNumber
                min={0}
                value={discount.amount / 100}
                onChange={(value) => {
                  discount.amount = (value || 0) * 100 // Convert dollar to cent
                }}
                formatter={this.formatter}
              />
            )}

            <Select<DiscountModels.DiscountTypes>
              value={discount.type}
              onChange={(value) => {
                discount.type = value

                discount.amount =
                  discount.type === DiscountModels.DiscountTypes.FIXED
                    ? discount.amount * 100
                    : discount.amount / 100
              }}
            >
              <Select.Option value={DiscountModels.DiscountTypes.FIXED}>$</Select.Option>
              <Select.Option value={DiscountModels.DiscountTypes.PERCENT}>%</Select.Option>
            </Select>
          </Input.Group>
        </ResponsiveRow>

        <ResponsiveRow labelText={<strong>Automatic discount</strong>}>
          {discount.applyType === DiscountModels.ApplyTypes.PRODUCTS &&
          discount.loyaltyCost === 0 &&
          !discount.discountCode ? (
            <>
              <ButtonGroup>
                <Button
                  onClick={() => {
                    discount.automatic = true
                    if (!this.props.currentCompanyStore?.canUseAutoLoyaltyDiscount) {
                      discount.loyaltyTierId = null
                      this.props.setEnableLoyaltyTargeting(false)
                      discount.setIsLoyaltyDiscount(false)
                      discount.setLoyaltyCost(0)
                    }
                    discount.setSalesChannels([])
                  }}
                  type={discount.automatic === true ? 'primary' : undefined}
                >
                  On
                </Button>
                <Button
                  onClick={() => (discount.automatic = false)}
                  type={!discount.automatic ? 'primary' : undefined}
                >
                  Off
                </Button>
              </ButtonGroup>

              <div className='caption'>
                {discount.automatic
                  ? 'Discount is automatically applied to products at the time of sale'
                  : 'Discount can be manually applied at the time of sale'}
              </div>
            </>
          ) : (
            <>
              <Button disabled>Disabled</Button>

              <div className='caption'>
                {discount.applyType !== DiscountModels.ApplyTypes.PRODUCTS
                  ? 'Only product discounts can be automatic'
                  : discount.discountCode
                  ? 'Discounts with code cannot be automatic'
                  : discount.loyaltyCost !== 0
                  ? 'Discount cannot be automatic if loyalty points are required'
                  : ''}
              </div>
            </>
          )}
        </ResponsiveRow>

        {currentCompanyStore?.canUseSalesChannel && (
          <ResponsiveRow
            rowProps={{ style: { marginTop: 16 } }}
            labelProps={{ className: 'mt-0' }}
            labelText={<strong>Sales channels restrictions</strong>}
          >
            <div className='mb-2'>Available on</div>

            <Checkbox.Group
              value={discount.salesChannels.map((salesChannel) => salesChannel.id)}
              onChange={(salesChannelIds) => {
                const saleChannels = Object.values(DiscountModels.SalesChannels).filter(
                  (saleChannel) => salesChannelIds.includes(saleChannel.id),
                )

                discount.setSalesChannels(saleChannels)
              }}
            >
              <Row gutter={[12, 12]}>
                {Object.values(DiscountModels.SalesChannels).map((salesChannel) => (
                  <Col key={salesChannel.name} span={24}>
                    <Checkbox value={salesChannel.id} disabled={Boolean(discount.automatic)}>
                      {salesChannel.displayName}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>

            {discount.automatic && (
              <div className='caption'>Automatic discounts are applied to all sales channels</div>
            )}
          </ResponsiveRow>
        )}

        {!hasExternalLoyaltyEnabled && currentCompanyStore?.canUseDiscountCode && (
          <ResponsiveRow
            labelText={<strong>Discount Code</strong>}
            rowProps={{ style: { marginTop: 5 } }}
          >
            <Input
              className={`input-field ${isDiscountCodeValid === false ? 'error' : ''}`}
              value={discount.discountCode ?? ''}
              onChange={(e) => onDiscountCodeChange(e.target.value)}
              disabled={discount.automatic}
            />
            <div className='caption'>
              {discount.automatic
                ? "Discounts with code can't be automatic discounts"
                : 'Discount codes should be alphanumeric, uppercase and without blank spaces'}
            </div>
          </ResponsiveRow>
        )}
      </SingleCollapsibleContainer>
    )
  }
}
